import { assertColorVariable } from './concept';
import { CardStyle, UiStyle, UiStyleRadiusOption } from './model';
import { ColorVariable, CompiledScheme } from './types';

export const getUiStyleComponentSlug = (
  uiStyle: UiStyle,
  cardStyle: CardStyle,
) => `ui-style-${uiStyle}_${cardStyle}`;

export type UiStyleRadiusKey =
  | 'button'
  | 'tag'
  | 'form_input'
  | 'form_checkbox-icon'
  | 'tabs-menu'
  | 'tab-link'
  | 'category-filter-link'
  | 'slider-arrow';

export const UI_STYLE_RADIUS_PIXELS: Record<
  UiStyleRadiusOption,
  Record<UiStyleRadiusKey, number>
> = {
  [UiStyleRadiusOption.Radius0]: {
    button: 0,
    tag: 0,
    form_input: 0,
    'form_checkbox-icon': 0,
    'tabs-menu': 0,
    'tab-link': 0,
    'category-filter-link': 0,
    'slider-arrow': 0,
  },
  [UiStyleRadiusOption.Radius1]: {
    button: 6,
    tag: 4,
    form_input: 6,
    'form_checkbox-icon': 2,
    'tabs-menu': 6,
    'tab-link': 4,
    'category-filter-link': 4,
    'slider-arrow': 4,
  },
  [UiStyleRadiusOption.Radius2]: {
    button: 12,
    tag: 6,
    form_input: 12,
    'form_checkbox-icon': 4,
    'tabs-menu': 10,
    'tab-link': 8,
    'category-filter-link': 8,
    'slider-arrow': 8,
  },
  [UiStyleRadiusOption.Radius3]: {
    button: 100,
    tag: 100,
    form_input: 12,
    'form_checkbox-icon': 4,
    'tabs-menu': 100,
    'tab-link': 100,
    'category-filter-link': 100,
    'slider-arrow': 100,
  },
};

export const UI_STYLE_RADIUS_OVERRIDES_PIXELS: Record<
  UiStyle,
  Partial<Record<UiStyleRadiusKey, number>>
> = {
  [UiStyle.Default]: {},
  [UiStyle.Bubble]: {},
  [UiStyle.Brick]: {},
  [UiStyle.Gradient]: {},
  [UiStyle.Sleek]: {
    form_input: 0,
  },
  [UiStyle.Elevate]: {},
};

export const UI_STYLE_STYLED_CLASSES = new Set([
  'button',
  'tag',
  'form',
  'tabs-menu',
  'tab-link',
  'category-filter-menu',
  'category-filter-link',
  'slider-arrow',
  'form_checkbox',
  'form_checkbox-btn',
  'form_checkbox-btn-icon',
  'form_checkbox-btn-label',
  'form_checkbox-icon',
  'form_checkbox-label',
  'form_field-label',
  'form_input',
  'form_radio',
  'form_radio-btn',
  'form_radio-btn-icon',
  'form_radio-btn-label',
  'form_radio-btn-letter',
  'form_radio-icon',
  'form_radio-label',
  'form_toggle',
  'form_toggle-dot',
  'form_toggle-field',
  'form_toggle-icon',
  'form_toggle-label',
  'form_toggle-mask',
]);

export const reverseNeutralColorVariable = (
  shade: ColorVariable,
): ColorVariable => {
  const opacityMatch = shade.match(
    /^(neutral-white|neutral-shade-7)-([0-9]+)$/,
  );
  if (opacityMatch) {
    const [, base, opacity] = opacityMatch;
    switch (base) {
      case 'neutral-white':
        return assertColorVariable(`neutral-shade-7-${opacity}`);
      case 'neutral-shade-7':
        return assertColorVariable(`neutral-white-${opacity}`);
    }
  }

  switch (shade) {
    case 'neutral-white':
    case 'neutral-shade-1':
      return 'neutral-shade-7';
    case 'neutral-shade-2':
      return 'neutral-shade-6';
    case 'neutral-shade-3':
      return 'neutral-shade-5';
    case 'neutral-shade-4':
      return 'neutral-shade-4';
    case 'neutral-shade-5':
      return 'neutral-shade-3';
    case 'neutral-shade-6':
      return 'neutral-shade-2';
    case 'neutral-shade-7':
      return 'neutral-white';
    default:
      return shade;
  }
};

export const shouldStyleBeAlternate = (
  scheme: CompiledScheme,
  classNames: string[],
) => {
  if (scheme.alternate.has('form-input')) {
    const isFormInput = classNames.some((c) => c.match(/^form_(.*)/));
    const isNonPrimaryButton =
      classNames.includes('button') &&
      (classNames.includes('is-secondary') ||
        classNames.includes('is-tertiary') ||
        classNames.includes('is-link'));

    const isTag = classNames.includes('tag');

    if (isFormInput || isNonPrimaryButton || isTag) {
      return true;
    }
  }

  if (scheme.alternate.has('primary-button')) {
    const isPrimaryButton =
      classNames.includes('button') &&
      !(
        classNames.includes('is-secondary') ||
        classNames.includes('is-tertiary') ||
        classNames.includes('is-link')
      );

    if (isPrimaryButton) {
      return true;
    }
  }

  return false;
};
